import React, { useState, useEffect } from 'react';
import { SQLiteService } from '../services/SQLiteService';
import DatabaseView from './DatabaseView';

interface DocumentInfo {
  documentId: string;
  filename: string;
  size: number;
  lastModified: Date;
}

const DatabasesSection: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<string | null>(null);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const documentDatabases = await SQLiteService.listDocumentDatabases();
      setDocuments(documentDatabases);
      
      // Auto-select first document if none selected
      if (!selectedDocument && documentDatabases.length > 0) {
        setSelectedDocument(documentDatabases[0].documentId);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const deleteDocument = async (documentId: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${documentId}" and all its data?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    setDeletingDocument(documentId);
    setError(null);

    try {
      const success = await SQLiteService.deleteDocumentDatabase(documentId);
      
      if (success) {
        // If deleted document was selected, clear selection
        if (selectedDocument === documentId) {
          setSelectedDocument(null);
        }
        await loadDocuments();
      } else {
        setError(`Failed to delete document: ${documentId}`);
      }
    } catch (error) {
      console.error(`Error deleting document ${documentId}:`, error);
      setError(`Error deleting document: ${documentId}`);
    } finally {
      setDeletingDocument(null);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="databases-container">
        <div className="loading">Loading databases...</div>
      </div>
    );
  }

  return (
    <div className="databases-container">
      {/* Sub-sidebar with database list */}
      <div className="databases-sidebar">
        <header className="databases-header">
          <h3>🗄️ Databases</h3>
          <button onClick={loadDocuments} className="refresh-btn" title="Refresh">
            🔄
          </button>
        </header>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <div className="databases-list">
          {documents.length === 0 ? (
            <div className="empty-state">
              <p>No databases found</p>
              <small>Create documents to see databases here</small>
            </div>
          ) : (
            documents.map((doc) => (
              <div 
                key={doc.documentId}
                className={`database-item ${selectedDocument === doc.documentId ? 'selected' : ''}`}
                onClick={() => setSelectedDocument(doc.documentId)}
              >
                <div className="database-info">
                  <div className="database-name">{doc.filename}</div>
                  <div className="database-meta">
                    <span className="database-size">{formatBytes(doc.size)}</span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteDocument(doc.documentId);
                  }}
                  className="delete-btn"
                  disabled={deletingDocument === doc.documentId}
                  title={`Delete ${doc.documentId}`}
                >
                  {deletingDocument === doc.documentId ? '⏳' : '🗑️'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main content area with database tables */}
      <div className="database-content">
        {selectedDocument ? (
          <div className="content-section">
            <header className="section-header">
              <h2>📊 {selectedDocument}</h2>
              <p>Database tables and contents</p>
            </header>
            <DatabaseView documentId={selectedDocument} />
          </div>
        ) : (
          <div className="no-selection">
            <div className="no-selection-content">
              <h3>Select a Database</h3>
              <p>Choose a database from the list to view its contents</p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .databases-container {
          display: flex;
          height: 100%;
          background-color: #f8f9fa;
        }

        .databases-sidebar {
          width: 300px;
          background: white;
          border-right: 1px solid #e1e5e9;
          display: flex;
          flex-direction: column;
        }

        .databases-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          border-bottom: 1px solid #e1e5e9;
          background: #f8f9fa;
        }

        .databases-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .refresh-btn {
          background: none;
          border: none;
          font-size: 1rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        }

        .refresh-btn:hover {
          background-color: #e9ecef;
        }

        .error-message {
          padding: 1rem;
          background-color: #f8d7da;
          color: #721c24;
          border-bottom: 1px solid #e1e5e9;
          font-size: 0.9rem;
        }

        .databases-list {
          flex: 1;
          overflow-y: auto;
        }

        .empty-state {
          padding: 2rem 1.5rem;
          text-align: center;
          color: #6c757d;
        }

        .empty-state p {
          margin: 0 0 0.5rem 0;
          font-weight: 500;
        }

        .empty-state small {
          font-size: 0.85rem;
          opacity: 0.8;
        }

        .database-item {
          display: flex;
          align-items: center;
          padding: 1rem 1.5rem;
          cursor: pointer;
          transition: background-color 0.2s ease;
          border-bottom: 1px solid #f1f3f4;
        }

        .database-item:hover {
          background-color: #f8f9fa;
        }

        .database-item.selected {
          background-color: #e3f2fd;
          border-left: 4px solid #2196f3;
        }

        .database-info {
          flex: 1;
          min-width: 0;
        }

        .database-name {
          font-weight: 500;
          color: #2c3e50;
          margin-bottom: 0.25rem;
          word-break: break-all;
        }

        .database-meta {
          display: flex;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #6c757d;
        }

        .database-size {
          font-weight: 500;
        }

        .delete-btn {
          padding: 0.375rem 0.5rem;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease-in-out;
          margin-left: 0.5rem;
        }

        .delete-btn:hover:not(:disabled) {
          background-color: #c82333;
          transform: scale(1.05);
        }

        .delete-btn:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .database-content {
          flex: 1;
          overflow-y: auto;
          background: white;
        }

        .content-section {
          padding: 2rem;
          max-width: none;
          margin: 0;
        }

        .section-header {
          margin-bottom: 2rem;
        }

        .section-header h2 {
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .section-header p {
          color: #7f8c8d;
          margin: 0;
          font-size: 1rem;
        }

        .no-selection {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f8f9fa;
        }

        .no-selection-content {
          text-align: center;
          color: #6c757d;
        }

        .no-selection-content h3 {
          margin: 0 0 1rem 0;
          color: #495057;
        }

        .no-selection-content p {
          margin: 0;
        }

        .loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #6c757d;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .databases-container {
            flex-direction: column;
          }

          .databases-sidebar {
            width: 100%;
            max-height: 40vh;
          }

          .databases-header {
            padding: 1rem;
          }

          .database-item {
            padding: 0.75rem 1rem;
          }

          .content-section {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DatabasesSection;
